/**
 * A replay's cars on the track map (race-replay.ts): where each car is at race time t, from its
 * timing, placed so it moves as tracked cars do.
 *
 * Between two timing points (the line, and the timing lines between when the timing gives sector
 * times: IMSA's IM1 and IM2) a car covers the distance in the time its timing says, at the circuit's
 * speed profile: slower where the outline bends, faster on the straights, braking and accelerating
 * at a racing car's rates (speedProfile). So cars close up in the corners and spread on the
 * straights, and each still crosses each line at its timed moment.
 *
 * Pit stops: on an in-lap (a lap that ends in the pit lane) the car runs to the pit-in point at its
 * last green lap's pace and is in the pit lane until, on its out-lap, it leaves the pit-out point in
 * time to reach the line at that pace. A car with no lap to come is drawn until a lap after its
 * last (its cool-down lap, or the lap on which it stopped), then no longer.
 */
import type { RaceChartData } from "@shared/types";
import type { TrackOutline } from "../chart/live-field";
import { IN_PIT, type CarPlace, type PitLane } from "../chart/track-motion";
import type { ReplayTimeline } from "./race-replay";

/** A circuit for the replay map: the outline, the pit lane on the lap, the timing lines between (m past the line). */
export interface ReplayTrack {
  outline: TrackOutline;
  pit: PitLane | null;
  /** Where the timing lines between are (m past the line, on this outline), for sector times s1, s2 (s3 to the line). */
  sectors?: [number, number] | null;
  /** A schematic loop of the lap's length: the circuit's shape is not known yet. */
  schematic?: boolean;
  /** The credit the map data needs, shown under the map. */
  attribution?: string | null;
}

/** The profile's step (m). */
const STEP_M = 5;
/** Grip (m/s² across the car), top speed, acceleration and braking (m/s²): a GT car's, roughly. */
const LAT_G = 14;
const TOP_MS = 78;
const ACCEL = 5;
const BRAKE = 11;
/** Bends are measured over this much track either side (m). */
const BEND_SPAN_M = 20;
/** m/s per mph. */
const MPH = 0.44704;

/** The circuit's speed profile: the share of a lap's time taken to reach each metre (every STEP_M). */
export interface SpeedProfile {
  len: number;
  /** Cumulative time share at each step (0 at the line, 1 at the end of the lap). */
  share: Float64Array;
  /** Speed at each step, as a share of the lap's average speed. */
  rel: Float64Array;
}

function pointOn(o: TrackOutline, d: number): [number, number] {
  const x = ((d % o.len) + o.len) % o.len;
  let lo = 0;
  let hi = o.ds.length - 1;
  while (lo < hi - 1) {
    const m = (lo + hi) >> 1;
    if (o.ds[m] <= x) lo = m;
    else hi = m;
  }
  const a = o.pts[lo];
  const b = o.pts[(lo + 1) % o.pts.length];
  const seg = (o.ds[lo + 1] ?? o.len) - o.ds[lo];
  const f = seg > 0 ? (x - o.ds[lo]) / seg : 0;
  return [a[0] + (b[0] - a[0]) * f, a[1] + (b[1] - a[1]) * f];
}

export function speedProfile(o: TrackOutline): SpeedProfile {
  const n = Math.max(8, Math.round(o.len / STEP_M));
  const step = o.len / n;
  const v = new Float64Array(n);
  for (let i = 0; i < n; i++) {
    const d = i * step;
    const [ax, ay] = pointOn(o, d - BEND_SPAN_M);
    const [bx, by] = pointOn(o, d);
    const [cx, cy] = pointOn(o, d + BEND_SPAN_M);
    const h1 = Math.atan2(by - ay, bx - ax);
    const h2 = Math.atan2(cy - by, cx - bx);
    let turn = Math.abs(h2 - h1);
    if (turn > Math.PI) turn = 2 * Math.PI - turn;
    const radius = turn > 1e-4 ? BEND_SPAN_M / turn : Infinity;
    v[i] = Math.min(TOP_MS, Math.sqrt(LAT_G * radius));
  }
  // Braking into and accelerating out of the bends (twice round, so it carries over the line).
  for (let pass = 0; pass < 2; pass++) {
    for (let k = 1; k <= n; k++) {
      const i = k % n;
      const p = (k - 1) % n;
      v[i] = Math.min(v[i], Math.sqrt(v[p] * v[p] + 2 * ACCEL * step));
    }
    for (let k = n - 1; k >= -1; k--) {
      const i = (k + n) % n;
      const nx = (i + 1) % n;
      v[i] = Math.min(v[i], Math.sqrt(v[nx] * v[nx] + 2 * BRAKE * step));
    }
  }
  const share = new Float64Array(n + 1);
  for (let i = 0; i < n; i++) share[i + 1] = share[i] + step / v[i];
  const total = share[n];
  for (let i = 0; i <= n; i++) share[i] /= total;
  const avg = o.len / total;
  const rel = new Float64Array(n);
  for (let i = 0; i < n; i++) rel[i] = v[i] / avg;
  return { len: o.len, share, rel };
}

/** The time share of the lap at metre d. */
function shareAt(p: SpeedProfile, d: number): number {
  const x = Math.min(p.len, Math.max(0, d));
  const n = p.share.length - 1;
  const f = (x / p.len) * n;
  const i = Math.min(n - 1, Math.floor(f));
  return p.share[i] + (p.share[i + 1] - p.share[i]) * (f - i);
}

/** The metre at time share s of the lap. */
function distAt(p: SpeedProfile, s: number): number {
  const x = Math.min(1, Math.max(0, s));
  let lo = 0;
  let hi = p.share.length - 1;
  while (lo < hi - 1) {
    const m = (lo + hi) >> 1;
    if (p.share[m] <= x) lo = m;
    else hi = m;
  }
  const span = p.share[hi] - p.share[lo];
  const f = span > 0 ? (x - p.share[lo]) / span : 0;
  return ((lo + f) / (p.share.length - 1)) * p.len;
}

/** From metre d0 at time t0 to d1 at t1 (d1 > d0 on this lap), along the profile: the metre at time t. */
function along(p: SpeedProfile, d0: number, t0: number, d1: number, t1: number, t: number): number {
  if (t1 <= t0) return d1;
  const s0 = shareAt(p, d0);
  const s1 = shareAt(p, d1);
  return distAt(p, s0 + (s1 - s0) * Math.min(1, Math.max(0, (t - t0) / (t1 - t0))));
}

/** Its speed (mph) at metre d on a lap of lapS seconds. */
function mphAt(p: SpeedProfile, d: number, lapS: number): number {
  const n = p.rel.length;
  const i = Math.min(n - 1, Math.max(0, Math.floor((d / p.len) * n)));
  return Math.round(((p.rel[i] * p.len) / lapS) / MPH);
}

/** A first pit lap longer than this many green laps is a start from the pit lane. */
const PIT_START_LAPS = 1.5;

/** Pit lane fallback when the circuit's is not known: in 250 m before the line, out 300 m after it. */
const PIT_IN_BEFORE_M = 250;
const PIT_OUT_AFTER_M = 300;

/** Every car's place at race time t ([car, m past the line, mph, flags]), as the live positions come. */
export function replayPlaces(data: RaceChartData, tl: ReplayTimeline, track: ReplayTrack, profile: SpeedProfile, t: number): CarPlace[] {
  const len = track.outline.len;
  const pitIn = track.pit?.in ?? len - PIT_IN_BEFORE_M;
  const pitOut = track.pit?.out ?? PIT_OUT_AFTER_M;
  const out: CarPlace[] = [];
  for (const [k, car] of Object.entries(data.cars)) {
    const ends = tl.ends[k];
    const laps = car.laps;
    if (!ends || !laps.length) continue;
    const label = car.label ?? String(car.num);
    let n = 0; // laps done by t
    while (n < ends.length && ends[n] <= t) n++;
    // Green pace for the pit lane and a cool-down lap: its last green lap before this one, else its
    // next one (a car that started from the pit lane has none before), else this one.
    const green = (j: number) => laps[j].pit === 0 && laps[j].ltSec > 0 && !/FCY|YELLOW/i.test(laps[j].flag) && (j === 0 || laps[j - 1].pit === 0);
    const refLap = (i: number) => {
      for (let j = Math.min(i, laps.length) - 1; j >= 0; j--) if (green(j)) return laps[j].ltSec;
      for (let j = i + 1; j < laps.length; j++) if (green(j)) return laps[j].ltSec;
      return laps[i]?.ltSec ?? laps[laps.length - 1].ltSec;
    };
    if (n >= ends.length) {
      // No lap to come: its cool-down lap (or the lap on which it stopped), then gone.
      const lapS = refLap(n);
      const since = t - ends[n - 1];
      if (since > lapS) continue;
      const d = along(profile, 0, 0, len, lapS * 1.3, since);
      out.push([label, Math.round(d), mphAt(profile, d, lapS * 1.3), 0]);
      continue;
    }
    const lap = laps[n];
    const end = ends[n];
    const start = n > 0 ? ends[n - 1] : end - lap.ltSec;
    if (t < start) {
      // On the grid: at the line.
      out.push([label, 0, 0, 0]);
      continue;
    }
    const lapS = Math.max(1, refLap(n));
    // A first lap marked as a pit lap and far longer than a lap: it started from the pit lane (held
    // there, or in the garage, until it joined), so it runs as an out-lap.
    const pitStart = n === 0 && lap.pit === 1 && lap.ltSec > PIT_START_LAPS * lapS;
    const prevPit = (n > 0 && laps[n - 1].pit === 1) || pitStart;
    if ((lap.pit === 1 && !pitStart) || prevPit) {
      // In-lap: to the pit-in point at its pace, then the pit lane. Out-lap: the pit lane, then from
      // the pit-out point at its pace.
      if (lap.pit === 1 && !prevPit) {
        const reach = start + lapS * shareAt(profile, pitIn);
        if (t < reach) {
          const d = along(profile, 0, start, pitIn, reach, t);
          out.push([label, Math.round(d), mphAt(profile, d, lapS), 0]);
        } else out.push([label, Math.round(pitIn), 0, IN_PIT]);
      } else {
        const leave = end - lapS * (1 - shareAt(profile, pitOut));
        if (t < leave) out.push([label, Math.round(pitOut), 0, IN_PIT]);
        else {
          const d = along(profile, pitOut, leave, len, end, t);
          out.push([label, Math.round(d), mphAt(profile, d, lapS), 0]);
        }
      }
      continue;
    }
    // Racing: through the timing points the timing gives, at the circuit's profile between them.
    const pts: [number, number][] = [[0, start]];
    const s = track.sectors;
    if (s && lap.s1 && lap.s2 && lap.s3) {
      const scale = lap.ltSec / (lap.s1 + lap.s2 + lap.s3);
      pts.push([s[0], start + lap.s1 * scale], [s[1], start + (lap.s1 + lap.s2) * scale]);
    }
    pts.push([len, end]);
    let i = 0;
    while (i < pts.length - 2 && t > pts[i + 1][1]) i++;
    const [d0, t0] = pts[i];
    const [d1, t1] = pts[i + 1];
    const d = along(profile, d0, t0, d1, t1, t);
    out.push([label, Math.round(d) % Math.round(len), mphAt(profile, d, lap.ltSec), 0]);
  }
  return out;
}
